import type { TFunction } from "i18next";
import {
	EventCategory,
	MeasurementSystem,
	VelavuAsset,
	VelavuDevice,
	VelavuEvent,
} from "velavu-js-api";
import {
	AnalyticsGraphEntry,
	AnalyticsGraphUnitRange,
} from "./analytics-graph";
import { formatTemperature } from "../../helper/unit-helper";

export interface AnalyticsGraphData {
	/// The lower and upper range of the graph
	unitRange: AnalyticsGraphUnitRange;
	/// The points to show on the graph
	data: AnalyticsGraphEntry[];
	/// The suffix to apply to numeric values in the graph
	unitSuffix: string;
	/// The title of the graph
	title: string;
}

interface HasNameOrID {
	name?: string;
	id: string;
}

export interface AnalyticsGraphDataParams<V> {
	entries: HasNameOrID[] | undefined;
	measurementSystem: MeasurementSystem;
	data: { [key: string]: V[] };
}

export function analyticsGraphDataFromTemperatureData(
	params: AnalyticsGraphDataParams<VelavuEvent<EventCategory.Environment>>,
	t: TFunction,
): AnalyticsGraphData {
	const graphData = Object.entries(params.data)
		.map(([id, events]): AnalyticsGraphEntry => {
			return {
				id: id,
				name: params.entries?.find((e) => e.id === id)?.name ?? id,
				data: events
					.map((event) => {
						if (event.data.temperature_c === undefined) {
							return undefined;
						}

						return {
							x: new Date(event.timestamp),
							y: formatTemperature(
								event.data.temperature_c,
								params.measurementSystem,
							),
						};
					})
					.filter((data) => data !== undefined),
			};
		})
		.filter(({ data }) => data.length !== 0);

	//Resolve the temperature range
	const unitRange = Object.entries(
		params.data,
	).reduce<AnalyticsGraphUnitRange>(
		(range, [_id, events]) => {
			for (const event of events) {
				const temp = event.data.temperature_c;
				if (temp !== undefined) {
					range.min = Math.min(range.min, temp);
					range.max = Math.max(range.max, temp);
				}
			}
			return range;
		},
		{ min: 15, max: 30 },
	);
	unitRange.min = formatTemperature(unitRange.min, params.measurementSystem);
	unitRange.max = formatTemperature(unitRange.max, params.measurementSystem);

	return {
		unitRange,
		data: graphData,
		unitSuffix: "°",
		title: t("analytics.temperature_history"),
	};
}

export function analyticsGraphDataFromHumidityData(
	params: AnalyticsGraphDataParams<VelavuEvent<EventCategory.Environment>>,
	t: TFunction,
): AnalyticsGraphData {
	const graphData = Object.entries(params.data)
		.map(([id, events]): AnalyticsGraphEntry => {
			return {
				id: id,
				name: params.entries?.find((e) => e.id === id)?.name ?? id,
				data: events
					.map((event) => {
						if (event.data.humidity === undefined) {
							return undefined;
						}

						return {
							x: new Date(event.timestamp),
							y: event.data.humidity,
						};
					})
					.filter((data) => data !== undefined),
			};
		})
		.filter(({ data }) => data.length !== 0);

	const unitRange = Object.entries(
		params.data,
	).reduce<AnalyticsGraphUnitRange>(
		(range, [_id, events]) => {
			for (const event of events) {
				const humidity = event.data.humidity;
				if (humidity !== undefined) {
					range.min = Math.min(range.min, humidity);
					range.max = Math.max(range.max, humidity);
				}
			}
			return range;
		},
		{ min: 0, max: 100 },
	);

	return {
		unitRange,
		data: graphData,
		unitSuffix: "%",
		title: t("analytics.humidity_history"),
	};
}

export function analyticsGraphDataFromBatteryData(
	params: AnalyticsGraphDataParams<VelavuEvent<EventCategory.BatteryLevel>>,
	t: TFunction,
): AnalyticsGraphData {
	const graphData = Object.entries(params.data)
		.map(([id, events]): AnalyticsGraphEntry => {
			return {
				id: id,
				name: params.entries?.find((e) => e.id === id)?.name ?? id,
				data: events
					.map((event) => {
						if (event.data.battery_level === undefined) {
							return undefined;
						}

						return {
							x: new Date(event.timestamp),
							y: event.data.battery_level,
						};
					})
					.filter((data) => data !== undefined),
			};
		})
		.filter(({ data }) => data.length !== 0);

	return {
		unitRange: { min: 0, max: 100 },
		data: graphData,
		unitSuffix: "%",
		title: t("analytics.battery_history"),
	};
}
